import React from 'react';
import { MutationFunction } from '@apollo/client';
import { useRouter } from 'next/router';
import { useTranslate } from 'nucleus-text';
import { IdpLoginMutation, IdpLoginMutationVariables } from '@cvent/universal-login-web-graphql/operations';

import { transferStateHandler } from '@utils/TransferStateHelpers';
import { useAlert, useError } from '@components/providers/ErrorProvider';
import { useSsoLogin, useTransferId } from '@components/providers/FormDisplayProvider';
import { useTargetProduct } from '@components/providers/ProductProvider';
import { LoginForm } from '@components/forms/LoginForm';
import { IdentityProviderNameTextbox } from '@components/inputFields/IdentityProviderNameTextbox';
import { ResetLoginTextLink } from '@components/textFields/ResetLoginTextLink';
import { Subtitle } from '@components/textFields/Subtitle';
import { Title } from '@components/textFields/Title';

type SsoLoginFormProps = {
  testID: string;
  redirectMutation: MutationFunction<IdpLoginMutation, Omit<IdpLoginMutationVariables, 'targetProductId'>>;
};
type FormValues = {
  identityProviderName: string;
};

export const SsoLoginForm = ({ testID, redirectMutation }: SsoLoginFormProps): JSX.Element => {
  const router = useRouter();
  const { translate } = useTranslate();
  const { targetProduct } = useTargetProduct();
  const { ssoLogin } = useSsoLogin();
  const { updateTransferId } = useTransferId();
  const { updateAlert } = useAlert();
  const { updateError } = useError();
  const initialValues: FormValues = { identityProviderName: '' };

  if (!ssoLogin) return null;

  const onSubmit = async (_, { values }): Promise<void> => {
    updateAlert({});

    const { data } = await redirectMutation({ variables: { ...(values as FormValues) } });

    await transferStateHandler({
      loginResponse: data.idpTransferState,
      router,
      updateTransferId,
      updateAlert,
      updateError,
      errorProps: { targetProductName: targetProduct.label }
    });
  };

  return (
    <LoginForm testID={testID} initialValues={initialValues} onSubmit={onSubmit}>
      {{
        title: <Title testID={testID} text={translate('form_title', { targetProduct: targetProduct.label })} />,
        subtitle: <Subtitle testID={testID} text={translate('form_sso_subtitle')} />,
        inputFields: [<IdentityProviderNameTextbox testID={testID} />],
        submitText: translate('nextButton__resx'),
        aboveDivider: [],
        belowDivider: [<ResetLoginTextLink testID={testID} />]
      }}
    </LoginForm>
  );
};
